/**
 * StatusEffects -- Manages status effect application, ticking, and expiration.
 *
 * Reads status effect definitions from status_effects.json via DataLoader.
 * Handles burn/poison damage, regen healing, stun AP loss, haste AP bonus,
 * and visual indicator updates.
 *
 * @module StatusEffects
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';

class StatusEffectsSystem {
  constructor() {
    /**
     * Reference to the CombatSystem.
     * @type {object|null}
     */
    this._combatSystem = null;
  }

  /**
   * Initialize with a reference to the CombatSystem.
   * @param {object} combatSystem
   */
  init(combatSystem) {
    this._combatSystem = combatSystem;
  }

  /**
   * Apply a status effect to a combatant.
   * Handles stacking, duration refresh, and logging.
   * @param {object} target - The combatant receiving the effect
   * @param {string} statusId - Status effect ID from status_effects.json
   * @param {number} duration - Number of turns
   * @param {object} [source] - The combatant that applied this effect
   */
  applyStatus(target, statusId, duration, source = null) {
    if (!target || !target.alive) return;

    const statusData = dataStore.getById('status_effects', statusId);
    const effectName = statusData ? statusData.name : statusId;
    const stackable = statusData ? statusData.stackable : false;
    const effects = statusData ? [...statusData.effects] : [];

    // Check for existing instance
    const existing = target.statusEffects.find(e => e.id === statusId);

    if (existing && !stackable) {
      // Refresh duration to the longer one
      existing.duration = Math.max(existing.duration, duration);
      this._log(`${target.name}'s ${effectName} duration refreshed (${existing.duration} turns).`);
    } else {
      // Add new instance
      const effect = {
        id: statusId,
        name: effectName,
        duration,
        effects,
        source: source ? source.id : null,
        _absorbRemaining: statusId === 'shielded' ? 20 : undefined,
      };
      target.statusEffects.push(effect);
      this._log(`${target.name} is now ${effectName}!`);
    }

    eventBus.emit('combat:status-apply', {
      target,
      statusId,
      name: effectName,
      duration,
      source,
    });
  }

  /**
   * Remove a specific status effect from a combatant.
   * @param {object} target
   * @param {string} statusId
   */
  removeStatus(target, statusId) {
    const idx = target.statusEffects.findIndex(e => e.id === statusId);
    if (idx === -1) return;

    const removed = target.statusEffects[idx];
    target.statusEffects.splice(idx, 1);

    this._log(`${removed.name} wears off on ${target.name}.`);

    eventBus.emit('combat:status-expire', {
      target,
      statusId,
      name: removed.name,
    });
  }

  /**
   * Remove all status effects from a combatant.
   * @param {object} target
   */
  clearAllStatuses(target) {
    const names = target.statusEffects.map(e => e.name);
    target.statusEffects = [];
    if (names.length > 0) {
      this._log(`All status effects cleared from ${target.name}.`);
    }
  }

  /**
   * Check if a combatant has a specific status effect.
   * @param {object} combatant
   * @param {string} statusId
   * @returns {boolean}
   */
  hasStatus(combatant, statusId) {
    return combatant.statusEffects.some(e => e.id === statusId);
  }

  /**
   * Get all active status effects on a combatant.
   * @param {object} combatant
   * @returns {object[]}
   */
  getStatuses(combatant) {
    return combatant.statusEffects || [];
  }

  /**
   * Process all status effects at the start of a combatant's turn.
   * Applies per-turn damage, healing, AP modifications.
   * Decrements durations and removes expired effects.
   * @param {object} combatant
   */
  tickStatuses(combatant) {
    if (!combatant.alive) return;

    const expiring = [];

    for (const effect of combatant.statusEffects) {
      // Process per-turn effects
      if (effect.effects) {
        for (const e of effect.effects) {
          if (e.type === 'damagePerTurn') {
            const dmg = e.value || 5;
            combatant.hp -= dmg;
            combatant.hp = Math.max(0, combatant.hp);
            this._log(`${combatant.name} takes ${dmg} ${e.damageType || ''} damage from ${effect.name}.`);

            eventBus.emit('combat:status-tick', {
              combatant,
              statusId: effect.id,
              type: 'damage',
              value: dmg,
            });

            if (combatant.hp <= 0) {
              combatant.alive = false;
              this._log(`${combatant.name} has been defeated by ${effect.name}!`);
              eventBus.emit('combat:kill', { attacker: null, target: combatant });
              return; // Dead, stop processing
            }
          }

          if (e.type === 'healPerTurn') {
            const heal = e.value || 5;
            combatant.hp = Math.min(combatant.maxHp, combatant.hp + heal);
            this._log(`${combatant.name} regenerates ${heal} HP from ${effect.name}.`);

            eventBus.emit('combat:status-tick', {
              combatant,
              statusId: effect.id,
              type: 'heal',
              value: heal,
            });
          }

          if (e.type === 'loseAllAP') {
            combatant.ap = 0;
            this._log(`${combatant.name} is stunned and loses all AP!`);

            eventBus.emit('combat:status-tick', {
              combatant,
              statusId: effect.id,
              type: 'stun',
              value: 0,
            });
          }

          if (e.type === 'bonusAP') {
            combatant.ap += (e.value || 0);
            combatant.maxAp = combatant.ap; // Track for display
            this._log(`${combatant.name} gains +${e.value} AP from ${effect.name}.`);
          }
        }
      }

      // Decrement duration
      if (effect.duration > 0) {
        effect.duration--;
        if (effect.duration <= 0) {
          expiring.push(effect);
        }
      }
      // duration === -1 means infinite (shielded, etc.)
    }

    // Remove expired effects
    for (const exp of expiring) {
      target: {
        const idx = combatant.statusEffects.indexOf(exp);
        if (idx !== -1) {
          combatant.statusEffects.splice(idx, 1);
        }
      }
      this._log(`${exp.name} wears off on ${combatant.name}.`);

      eventBus.emit('combat:status-expire', {
        target: combatant,
        statusId: exp.id,
        name: exp.name,
      });
    }
  }

  /**
   * Log a message via CombatSystem if available.
   * @param {string} msg
   */
  _log(msg) {
    if (this._combatSystem) {
      this._combatSystem._addLog(msg);
    }
  }
}

/** Singleton instance. */
const statusEffectsSystem = new StatusEffectsSystem();
export default statusEffectsSystem;
export { StatusEffectsSystem };
